import config from '../config'
import { PURGATORY_MAINTENANCE_TOKEN } from '../store/middleware/APIUrl'
import { attachChannelToDevice } from './content'
import { Callbacks } from './constants'
import { Types as DeviceTypes, createOrRestoreDevice, deviceUpdate } from './device'
import { connectBlocking, updateSettings, updateContent } from './socket'
import { getCallbackFromOpts } from './utils'
import { createAction } from 'redux-api-middleware'
import * as PurgatorySelectors from '../reducers/purgatory'

const PurgatorySteps = {
    ATTACH_CHANNEL: 'purgatory-attach-channel',
    ADD_PURGATORY: 'purgatory-add-device-purgatory',
    ADD_ESTATE: 'purgatory-add-device-estate',
    CLEAN_PURGATORY: 'purgatory-clean-purgatory',
    CONNECT_SOCKET_SERVER: 'purgatory-connect-socket-server',
    DONE: 'purgatory-done',
    PUSH_CONTENT: 'purgatory-push-content',
    PUSH_SETTINGS: 'purgatory-push-settings',
    UPDATE_URLS: 'purgatory-update-urls',
}

const ActionTypes = {
    FETCH_FIXTURE_CODE_REQUEST: 'FETCH_FIXTURE_CODE##REQUEST',
    FETCH_FIXTURE_CODE_SUCCESS: 'FETCH_FIXTURE_CODE##SUCCESS',
    FETCH_FIXTURE_CODE_FAILURE: 'FETCH_FIXTURE_CODE##FAILURE',
    CONFIGURE_DEVICE_UNNATENDENDLY_REQUEST: 'CONFIGURE_DEVICE_UNNATENDENDLY##REQUEST',
    CONFIGURE_DEVICE_UNNATENDENDLY_SUCCESS: 'CONFIGURE_DEVICE_UNNATENDENDLY##SUCCESS',
    CONFIGURE_DEVICE_UNNATENDENDLY_FAILURE: 'CONFIGURE_DEVICE_UNNATENDENDLY##FAILURE',
}

function sleep(seconds) {
    return new Promise((resolve) => setTimeout(resolve, seconds * 1000))
}

/**
 * Builds the details of the device to be added into the purgatory
 * @param {Object} device
 */
function buildPurgatoryDevice(device) {
    return {
        ...device,
        api_url: config.app.purgatoryUrl,
        device_type: 'UNKNOWN',
        display_group: 1,
        file_url: config.app.purgatoryUrl,
        socket_url: config.app.purgatorySocketUrl,
        store_code: '1',
        store_location: '1',
        sync_url: config.app.purgatorySocketUrl,
    }
}

/**
 * Updated the purgatory device with the urls to the right estate
 * @param {Object} device
 * @param {Object} devicePurgatory
 */
function updateUrlsInPurgatory(device, devicePurgatory) {
    const { api_url, socket_url, sync_url, file_url } = device
    const updatedDevice = {
        ...devicePurgatory,
        api_url,
        socket_url,
        sync_url,
        file_url,
    }

    return deviceUpdate(updatedDevice, { url: PURGATORY_MAINTENANCE_TOKEN })
}

/**
 * Transfers a device from purgatory to selected estate
 * @param {Object} device
 * @param {Number} [channelUdId]
 * @param {Object} [opts]
 * @param {Function} [opts.onError]
 * @param {Function} [opts.onProgress]
 * @param {string} [opts.socket]
 * @param {string} [opts.url]
 */
function transferDevice(device, channelId = null, opts = {}) {
    if (!device || typeof device !== 'object') {
        throw new Error(`Wrong device type. Got: ${typeof device}`)
    }

    const { url, socket } = opts
    const {
        app: { purgatorySocketUrl, purgatorySocketPostfix },
    } = config

    const purgatorySocket = `${purgatorySocketUrl}${purgatorySocketPostfix}`
    let destinationUrl = url

    if (typeof destinationUrl === 'string' && !destinationUrl.match(/\/maintenance/)) {
        destinationUrl = destinationUrl.replace(/^\/+/, '').replace('hubble', '') + '/maintenance'
    }

    let destinationSocketUrl = socket

    if (typeof destinationSocketUrl === 'string' && !destinationSocketUrl.match(new RegExp(`${purgatorySocketPostfix}`))) {
        destinationSocketUrl = destinationSocketUrl.replace(/^\/+/, '') + purgatorySocketPostfix
    }

    const { id: deviceId } = device
    const devicePurgatory = buildPurgatoryDevice(device)
    const onError = getCallbackFromOpts(opts, Callbacks.ON_ERROR)
    const onProgress = getCallbackFromOpts(opts, Callbacks.ON_PROGRESS)
    const numSteps = typeof channelId === 'number' ? 7 : 5

    return async (dispatch) => {
        onProgress(0, PurgatorySteps.CONNECT_SOCKET_SERVER)

        // Connecting to socket server
        await dispatch(connectBlocking(purgatorySocket))

        onProgress(1 / numSteps, PurgatorySteps.ADD_PURGATORY)

        // Upsert the device in the purgatory
        let [ok] = await dispatch(createOrRestoreDevice(devicePurgatory, { url: PURGATORY_MAINTENANCE_TOKEN }))

        // If not successful, quit
        if (!ok) {
            onError(PurgatorySteps.ADD_PURGATORY)
            return
        }

        // Update progress percentage
        onProgress(2 / numSteps, PurgatorySteps.ADD_ESTATE)

        // Upsert the device in the selected estate
        ;[ok] = await dispatch(createOrRestoreDevice(device, { url: destinationUrl }))

        if (!ok) {
            onError(PurgatorySteps.ADD_ESTATE)
            return
        }

        // Update progress
        onProgress(3 / numSteps, PurgatorySteps.UPDATE_URLS)

        // Update the servers urls of the device in purgatory
        const updateUrlActions = await dispatch(updateUrlsInPurgatory(device, devicePurgatory))

        // Check if that went right
        if (updateUrlActions.type !== DeviceTypes.DEVICE_UPDATE_SUCCESS) {
            onError(PurgatorySteps.UPDATE_URLS)
            return
        }

        // Update progress
        onProgress(4 / numSteps, PurgatorySteps.PUSH_SETTINGS)

        await sleep(20)

        // Push purgatory settings to the device
        await dispatch(updateSettings(deviceId))

        await sleep(10)

        if (!channelId) {
            if (url) {
                await dispatch(connectBlocking(destinationSocketUrl))
            } else {
                await dispatch(connectBlocking())
            }

            await sleep(20)

            await dispatch(updateSettings(deviceId))

            // Update progress
            onProgress(5 / numSteps, PurgatorySteps.DONE)
            return
        }

        // Update progress
        onProgress(5 / numSteps, PurgatorySteps.ATTACH_CHANNEL)

        await dispatch(attachChannelToDevice(channelId, deviceId, { url: destinationUrl }))

        // Update progress
        onProgress(6 / numSteps, PurgatorySteps.PUSH_CONTENT)

        await dispatch(connectBlocking(destinationSocketUrl))

        await sleep(20)

        await dispatch(updateSettings(deviceId))

        await sleep(20)

        dispatch(updateContent(deviceId))

        await sleep(20)

        // Update progress
        onProgress(7 / numSteps, PurgatorySteps.DONE)
    }
}

function fetchFixtureCode(fixtureCode) {
    return createAction({
        endpoint: `${config.app.purgatoryUrl}/hubble/fixtures_config/${fixtureCode}`,
        method: 'GET',
        types: [ActionTypes.FETCH_FIXTURE_CODE_REQUEST, ActionTypes.FETCH_FIXTURE_CODE_SUCCESS, ActionTypes.FETCH_FIXTURE_CODE_FAILURE],
    })
}

/**
 *
 * @param {object} params
 * @param {string} params.fixtureCode
 * @param {string} params.storeCode
 * @param {string} params.sku
 * @param {string} params.mac
 * @param {Object} [opts]
 * @param {Function} [opts.onError]
 * @param {Function} [opts.onProgress]
 * @returns
 */
function transferDeviceV2({ fixtureCode, storeCode, sku, mac }, opts = {}) {
    const onError = getCallbackFromOpts(opts, Callbacks.ON_ERROR)

    return async (dispatch, getState) => {
        try {
            await dispatch(fetchFixtureCode(fixtureCode))
            const fixtureConfigError = PurgatorySelectors.getFixtureConfigError(getState())

            console.log(fixtureConfigError)

            if (fixtureConfigError) {
                onError('purgatory-fixture-config')
                return
            }

            const fixtureConfig = PurgatorySelectors.getFixtureConfig(getState())

            /**
             * Get default value from config
             * @param {string} key
             */
            const getFromDefConfig = (key) => {
                const fixtDef = fixtureConfig.defaults.find((def) => def.attribute_key === key)
                return fixtDef?.value
            }

            const device = {
                antenna_level: getFromDefConfig('DEVICE_ANTENNA_LEVEL'),
                api_url: fixtureConfig.estate.api_url,
                apk_version: getFromDefConfig('DEVICE_APK_VERSION'),
                auth_key: getFromDefConfig('DEVICE_AUTH_KEY'),
                background_colour: getFromDefConfig('DEVICE_BACKGROUND_COLOUR') || '000000',
                chain_id: getFromDefConfig('DEVICE_CHAIN_ID') || '1',
                disable_zoom: getFromDefConfig('DEVICE_ZOOM_DISABLED'),
                device_owner: getFromDefConfig('DEVICE_DEVICE_OWNER') || 'false',
                device_type: getFromDefConfig('DEVICE_DEVICE_TYPE') || 'UNKNOWN',
                display_group: getFromDefConfig('DEVICE_DISPLAY_GROUP'),
                file_auth: getFromDefConfig('DEVICE_FILE_AUTH'),
                file_url: fixtureConfig.estate.file_url,
                filter_level: device.filter_level || "20",
                four_k: getFromDefConfig('DEVICE_FOUR_K'),
                id: mac.replace(/:/g, ''),
                ip: '',
                layer_enabled: getFromDefConfig('DEVICE_LAYER_ENABLED') || 'true',
                layer_extra: getFromDefConfig('DEVICE_LAYER_EXTRA') || '{}',
                mac,
                migrated_at: Date.now(),
                manufacturer: getFromDefConfig('DEVICE_MANUFACTURER') || '',
                model: getFromDefConfig('DEVICE_MODEL') || '',
                ntp_url: getFromDefConfig('DEVICE_NTP_URL') || 'time.google.com',
                screen_rotation: getFromDefConfig('DEVICE_SCREEN_ROTATION'),
                selected_channel: getFromDefConfig('DEVICE_SELECTED_CHANNEL') || 'none',
                selected_type: getFromDefConfig('DEVICE_SELECTED_TYPE'),
                sku,
                socket_url: fixtureConfig.estate.socket_url,
                store_code: storeCode,
                store_location: getFromDefConfig('DEVICE_STORE_LOCATION'),
                store_sync: getFromDefConfig('DEVICE_STORE_SYNC'),
                sync_delay: getFromDefConfig('DEVICE_SYNC_DELAY'),
                sync_offset: getFromDefConfig('DEVICE_SYNC_OFFSET'),
                sync_timeout: getFromDefConfig('DEVICE_SYNC_TIMEOUT'),
                sync_url: fixtureConfig.estate.sync_url,
                test_device: getFromDefConfig('DEVICE_TEST_DEVICE'),
                volume: getFromDefConfig('DEVICE_VOLUME'),
                wifi_settings: getFromDefConfig('DEVICE_WIFI_SETTINGS') || '{}',
                working_close: getFromDefConfig('DEVICE_WORKING_CLOSE'),
                working_open: getFromDefConfig('DEVICE_WORKING_OPEN'),
            }

            const destinationApi = fixtureConfig.estate.api_url
            const destinationSocket = fixtureConfig.estate.socket_url

            await dispatch(
                transferDevice(device, fixtureConfig.fixture_id, {
                    ...opts,
                    url: destinationApi,
                    socket: destinationSocket,
                })
            )
        } catch (err) {
            console.error(err)
            onError(err.message)
        }
    }
}

/**
 * 
 * @param {object} options 
 * @param {string} options.fixtureCode 
 * @param {string} options.storeCode 
 * @param {string} options.sku 
 * @param {string} options.mac 
 * @returns 
 */
function configureDeviceUnattendedly({
    fixtureCode,
    storeCode,
    sku,
    mac,
}) {
    const deviceId = mac.replaceAll(':', '');
    const timestamp = Math.floor(Date.now() / 1000);

    return createAction({
        endpoint: `${config.app.purgatoryUrl}/device/${config.app.purgatoryDeviceId}/configure/${timestamp}`,
        body: JSON.stringify({
            id: deviceId,
            mac,
            ip: '',
            sku,
            fixture_code: fixtureCode,
            store_code: storeCode,
        }),
        method: 'POST',
        types: [
            ActionTypes.CONFIGURE_DEVICE_UNNATENDENDLY_REQUEST,
            ActionTypes.CONFIGURE_DEVICE_UNNATENDENDLY_SUCCESS,
            ActionTypes.CONFIGURE_DEVICE_UNNATENDENDLY_FAILURE,
        ],
    });
}

export { ActionTypes, configureDeviceUnattendedly, transferDevice, transferDeviceV2 }
